import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { cn } from '@/shared/utils';
import { Button, ItemsListEmptyState, Search } from '.';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

type DropdownItem = Record<string, unknown> & {
  value: string;
};

type Props<T = DropdownItem> = {
  value?: string;
  items: Array<T>;
  renderItem: (item: T) => React.ReactNode;
  renderSelectedValue?: (value: string) => React.ReactNode;
  placeholder?: string;
  floatingLabel?: string;
  className?: string;
  contentClassName?: string;
  contentViewportClassName?: string;
  contentItemClassname?: string;
  onPointerDown?: (value: string) => void;
  disabled?: boolean;
  disabledItemKey?: keyof T;
  searchFilter?: (item: T, search: string) => boolean;
  contentAlign?: 'start' | 'center' | 'end';
  onValueChange?: (value: string) => void;
  searchRightContent?: React.ReactNode;
  onSearchFocus?: () => void;
  onSearchBlur?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  getItemKey?: (item: T) => string;
  withCancelButton?: boolean;
  isOptionSelected?: (item: T, value: string | undefined) => boolean;
  isInvalid?: boolean;
};

const formFieldDropdownStyles = tv({
  slots: {
    trigger:
      'flex items-center justify-between gap-[0.5rem] headline4  w-full h-[6rem] rounded-[1.6rem] bg-muted px-[1.6rem] cursor-pointer',
    content: 'rounded-[1.6rem] shadow-sm p-0 overflow-hidden',
    option: 'cursor-pointer px-[2.4rem] py-[1.2rem]',
    searchContainer:
      'sticky top-0 left-0 right-0 z-20 p-[1.6rem] bg-popover border-b border-border -mx-1 mb-0 flex gap-[1.6rem]',
  },
  variants: {
    disabled: {
      false: {
        trigger: 'hover:bg-stroke',
      },
    },
  },
});

type SearchInputProps = {
  search: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  searchInputRef?: React.Ref<HTMLInputElement>;
  withCancelButton?: boolean;
  onCancelClick?: () => void;
  showCancelButton: boolean;
};

const SearchInput = ({
  search,
  onSearchChange,
  onFocus,
  onBlur,
  searchInputRef,
  withCancelButton,
  onCancelClick,
  showCancelButton,
}: SearchInputProps) => {
  const { t } = useTranslation();
  const { searchContainer } = formFieldDropdownStyles();

  return (
    <div className={searchContainer()}>
      <Search
        ref={searchInputRef}
        label={t('common.search')}
        value={search}
        onChange={onSearchChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {withCancelButton && (
        <Button
          onMouseDown={() => {
            onCancelClick?.();
          }}
          variant="outline"
          className={cn(
            'border-none bg-transparent shadow-none headline5',
            !showCancelButton && 'hidden'
          )}>
          {t('common.cancel')}
        </Button>
      )}
    </div>
  );
};

const SearchableSelect = <T extends DropdownItem = DropdownItem>({
  value,
  placeholder,
  floatingLabel,
  items,
  disabled,
  isInvalid,
  renderItem,
  renderSelectedValue,
  searchFilter,
  onValueChange,
  getItemKey,
  disabledItemKey,
  withCancelButton = false,
  className,
  contentClassName,
  contentViewportClassName,
  contentItemClassname,
  isOptionSelected,
}: Props<T>) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [internalSearch, setInternalSearch] = useState('');
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [open, setOpen] = useState(false);

  const filteredItems = useMemo(() => {
    if (!searchFilter || !internalSearch) return items;
    return items.filter(item => searchFilter(item, internalSearch));
  }, [items, searchFilter, internalSearch]);

  const handleSearchInputFocus = useCallback(() => {
    if (withCancelButton) {
      setShowCancelButton(true);
    }
  }, [withCancelButton]);

  const handleSearchInputBlur = useCallback(() => {
    if (withCancelButton) {
      setShowCancelButton(false);
    }
  }, [withCancelButton]);

  const handleCancelClick = useCallback(() => {
    setShowCancelButton(false);
    setInternalSearch('');
    searchInputRef.current?.blur();
  }, [searchInputRef]);

  const handleOptionSelect = useCallback(() => {
    if (withCancelButton) {
      setShowCancelButton(false);
    }
    setInternalSearch('');
    setOpen(false);
  }, [withCancelButton]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalSearch(e.target.value);
    },
    []
  );

  const { trigger, content, option } = formFieldDropdownStyles();

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        disabled={disabled}
        floatingLabel={floatingLabel}
        placeholder={placeholder}
        size="md"
        value={value}
        className={cn(
          'headline4 w-full h-[6rem] rounded-[1.6rem] px-6 bg-muted',
          trigger({ disabled }),
          isInvalid && 'bg-light-error',
          className
        )}>
        {value && renderSelectedValue && renderSelectedValue(value)}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn(content(), contentClassName)}
        classNameViewport={cn(
          'overflow-y-auto bg-popover',
          searchFilter && 'pt-0',
          contentViewportClassName
        )}
        align="end"
        side="bottom"
        // prevent internal radix ui searching when you press any key to save focus on search input
        onKeyDownCapture={e => e.stopPropagation()}>
        {searchFilter && (
          <SearchInput
            search={internalSearch}
            onSearchChange={handleSearchChange}
            onFocus={handleSearchInputFocus}
            onBlur={handleSearchInputBlur}
            searchInputRef={searchInputRef}
            onCancelClick={handleCancelClick}
            withCancelButton={withCancelButton}
            showCancelButton={showCancelButton}
          />
        )}
        {filteredItems.length === 0 && (
          <ItemsListEmptyState
            title={t('common.noResults')}
            description={t('common.noResultsDescription')}
            className="py-[4.8rem] px-[3.2rem]"
          />
        )}
        {filteredItems.map(item => (
          <DropdownMenuItem
            disabled={disabledItemKey ? !!item[disabledItemKey] : false}
            key={getItemKey ? getItemKey(item) : item.value}
            className={cn(option(), contentItemClassname)}
            onSelect={() => {
              onValueChange?.(item.value);
              handleOptionSelect();
            }}
            showOptionCheckmark={
              isOptionSelected ? isOptionSelected(item, value) : false
            }
            // required to not lose search field focus when you move the cursor over the select content
            onPointerMove={e => e.preventDefault()}
            onPointerEnter={e => e.preventDefault()}
            onPointerLeave={e => e.preventDefault()}>
            {renderItem(item)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { SearchableSelect };
